import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IndexService } from '../../services';
import { IndexItem, isEphemeralIndex } from '../../shared/interfaces';
import { DeleteIndexModalComponent } from '../../views/index/delete-index-modal/delete-index-modal';
import { NewIndexModalComponent } from '../../views/index/new-index-modal/new-index-modal';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar implements OnInit {
  private readonly indexService = inject(IndexService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);

  // Stato interno
  indexes = signal<IndexItem[]>([]);
  isLoading = signal(true);
  expandedIndex = signal<string>('');
  selectedSubView = signal<'documents' | 'web-sites'>('documents');

  ngOnInit() {
    this.syncFromUrl();
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncFromUrl();
    });
    this.loadIndices();
  }

  private syncFromUrl() {
    const url = this.router.url.split('?')[0].split('#')[0];
    const segments = url.split('/').filter((s) => s.length > 0);
    if (segments.length > 0) {
      const indexName = decodeURIComponent(segments[0]);
      const subView = segments[1] === 'web-sites' ? 'web-sites' : 'documents';
      this.expandedIndex.set(indexName);
      this.selectedSubView.set(subView);
    }
  }

  loadIndices(preferredIndex?: string) {
    this.isLoading.set(true);
    this.indexService.getIndices().subscribe({
      next: (data) => {
        const filtered = (data || []).filter((item) => !isEphemeralIndex(item));
        this.indexes.set(filtered);
        this.isLoading.set(false);

        const currentUrl = this.router.url.split('?')[0].split('#')[0];
        const segments = currentUrl.split('/').filter((s) => s.length > 0);
        const urlIndex = segments.length > 0 ? decodeURIComponent(segments[0]) : '';
        const urlSubView = segments[1] === 'web-sites' ? 'web-sites' : 'documents';

        if (urlIndex && filtered.some((idx) => idx.name === urlIndex)) {
          this.expandedIndex.set(urlIndex);
          this.selectedSubView.set(urlSubView);
        } else if (filtered.length > 0) {
          const target = preferredIndex || filtered[0].name;
          this.expandedIndex.set(target);
          this.selectedSubView.set('documents');
          this.router.navigate(['/', target, 'documents', 'to-process']);
        } else {
          this.expandedIndex.set('');
        }
      },
      error: (err) => {
        console.error('Errore nel recupero degli indici nella sidebar:', err);
        this.isLoading.set(false);
      }
    });
  }

  toggleIndex(name: string) {
    this.expandedIndex.set(name);
    const view = this.selectedSubView();
    if (view === 'web-sites') {
      this.router.navigate(['/', name, 'web-sites']);
    } else {
      this.router.navigate(['/', name, 'documents', 'to-process']);
    }
  }

  selectSubView(indexName: string, subView: 'documents' | 'web-sites') {
    this.expandedIndex.set(indexName);
    this.selectedSubView.set(subView);
    if (subView === 'web-sites') {
      this.router.navigate(['/', indexName, 'web-sites']);
    } else {
      this.router.navigate(['/', indexName, 'documents', 'to-process']);
    }
  }

  openNewIndexModal() {
    const modalRef = this.modalService.open(NewIndexModalComponent, { centered: true });

    modalRef.result.then((result) => {
      if (result === true) {
        this.loadIndices();
      }
    }).catch(() => {});
  }

  openDeleteModal(event: Event, item: IndexItem) {
    event.stopPropagation();
    const modalRef = this.modalService.open(DeleteIndexModalComponent, { centered: true });
    modalRef.componentInstance.indexItem = item;

    modalRef.result.then((result) => {
      if (result === true) {
        this.loadIndices();
      }
    }).catch(() => {});
  }
}